import { useEffect, useLayoutEffect, useRef, useState } from 'react'

// Makes this page's own root the scrollport the summary bar's `position:
// sticky` (styles.tsx) binds to: nothing from <body> down bounds height or
// clips overflow, so without this the bar scrolls away with the document.
// Scoped to ManageLeat; the shared ManageRoot is untouched.
//
// Solves for the shell's unknown trailing chrome below <main> instead of
// hardcoding it: force the node to a known height, read back how much taller
// the document got, and the difference is exactly that chrome.

// Every antd click writes a wave <style> to <body> and empties it again when
// the animation ends (`antd/lib/_util/wave.js`) — layout-neutral either way.
const isStyleContent = (node: Node | null): boolean =>
  !!node && (node.nodeName === 'STYLE' || node.parentNode?.nodeName === 'STYLE')

const isLayoutNeutral = (node: Node): boolean =>
  isStyleContent(node) ||
  (node instanceof HTMLElement &&
    node.classList.contains('ant-click-animating-node'))

const areLayoutNeutral = (nodes: NodeList): boolean => {
  for (let index = 0; index < nodes.length; index += 1) {
    if (!isLayoutNeutral(nodes[index])) return false
  }
  return true
}

const movesLayout = (records: MutationRecord[]): boolean =>
  records.some((record) => {
    if (isStyleContent(record.target)) return false
    if (record.type !== 'childList') return true
    return (
      !areLayoutNeutral(record.addedNodes) ||
      !areLayoutNeutral(record.removedNodes)
    )
  })

// Any scrollTop write aborts a scroll still running, and measure() makes one
// whenever it clamped — so measuring waits this long after the last scroll.
const SCROLL_SETTLE_MS = 200

export const useManageRootScrollport = () => {
  const ref = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | null>(null)

  const measure = () => {
    const node = ref.current
    if (!node) return
    const previousScrollTop = node.scrollTop
    const previousHeight = node.style.height
    const previousOverflowY = node.style.overflowY
    // Guarantees the total exceeds innerHeight, so scrollHeight reports it
    // exactly instead of flooring at innerHeight — all a natural-height
    // reading gets once content already fits the page.
    const forcedHeight = window.innerHeight
    node.style.height = `${forcedHeight}px`
    node.style.overflowY = 'hidden'
    const scrollHeight = document.documentElement.scrollHeight
    node.style.height = previousHeight
    node.style.overflowY = previousOverflowY
    // Growing the node shrinks its own max scroll, and the browser clamps
    // scrollTop to it — by exactly the trailing chrome.
    if (node.scrollTop !== previousScrollTop) {
      node.scrollTop = previousScrollTop
    }
    // trailingChrome is scrollHeight - top - forcedHeight, and the target
    // height is innerHeight - top - trailingChrome — top cancels out of that,
    // so it's never actually read.
    setHeight(2 * window.innerHeight - scrollHeight)
  }

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    measure()

    // Deferred a frame: a burst of mutations (loading state, then data)
    // otherwise re-measures and re-renders once per mutation instead of once
    // for the settled result.
    let frame: number | null = null
    let timer: number | null = null
    let lastScrollAt = 0
    const scheduleMeasure = () => {
      if (frame !== null) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        frame = null
        const sinceScroll = Date.now() - lastScrollAt
        if (sinceScroll < SCROLL_SETTLE_MS) {
          if (timer !== null) clearTimeout(timer)
          timer = window.setTimeout(
            scheduleMeasure,
            SCROLL_SETTLE_MS - sinceScroll
          )
          return
        }
        measure()
      })
    }

    const onScroll = () => {
      lastScrollAt = Date.now()
    }
    node.addEventListener('scroll', onScroll, { passive: true })

    // Content height also changes without any prop changing (loading Spin →
    // content, a re-check filling an empty table).
    const observer = new MutationObserver((records) => {
      if (movesLayout(records)) scheduleMeasure()
    })
    // document.body, not just this node: an ancestor shift (e.g. DevInfoBanner
    // closing) moves `top` too, and no resize event fires for that.
    // Attributes excluded, or the height/overflow measure() writes here would
    // retrigger the observer.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    })
    return () => {
      observer.disconnect()
      node.removeEventListener('scroll', onScroll)
      if (frame !== null) cancelAnimationFrame(frame)
      if (timer !== null) clearTimeout(timer)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  return { ref, height }
}
